/**
 * `useGuidanceCommandsFromAgent` — let the concierge SHOW the lawyer a surface
 * instead of describing it.
 *
 * The motivating case (operator direction 2026-07-21): a lawyer asks "onde vejo
 * os e-mails do meu agente?" and the answer should not be a paragraph of
 * navigation instructions — the concierge should open the thing while it
 * explains. These tools are the "show, don't tell" half of that.
 *
 * Handled UI-command tools:
 *   - `lx_show_tasklist`       → opens the Pendências focus pane
 *   - `lx_show_agent_channel`  → `{channel: email|whatsapp|telegram}` — opens
 *                                the Agent page on that channel tab
 *   - `lx_show_agent_browser`  → opens the agent browser in the focus pane
 *
 * Agents activity is deliberately NOT here — `lx_show_team_pane` already owns
 * it (`useTeamPaneCommandsFromAgent`), and duplicating it would give the model
 * two ways to do one thing.
 *
 * These are **no-op tools on the runtime side**: they exist purely to ride the
 * gateway's automatic `session.tool` broadcast, exactly like `lx_open_file` and
 * `lx_show_team_pane`. This hook is the LexIDE half; the runtime registrations
 * are relayed separately (the SPA cannot register them — different lane).
 *
 * ⚠ Wire shape: the live broadcast carries the tool name at `data.name` and the
 * input at `data.args`; `toolName`/`toolInput` is the REKEYED shape that
 * message-content blocks get. `useTeamPaneCommandsFromAgent` records that five
 * sibling listeners read only the legacy fields and therefore miss the
 * broadcast silently — so this hook reads `name ?? toolName`, wire first.
 *
 * **Mount site:** the App chrome wrapper, globally — a guidance nudge has to
 * work from any route, not only inside a case.
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useEventStream } from "@/hooks/useEventStream";
import { useUIStore } from "@/stores/useUIStore";

/** The channel tabs the Agent page exposes (`AgentLayout` `?section=`). */
const AGENT_CHANNELS = ["email", "whatsapp", "telegram"] as const;
type AgentChannel = (typeof AGENT_CHANNELS)[number];

function isAgentChannel(v: unknown): v is AgentChannel {
  return typeof v === "string" && (AGENT_CHANNELS as readonly string[]).includes(v);
}

interface ToolEventPayload {
  data?: {
    phase?: string;
    name?: string;
    args?: Record<string, unknown>;
    toolName?: string;
    toolInput?: Record<string, unknown>;
  };
}

export interface GuidanceDeps {
  navigate: (path: string) => void;
  openFocusPane: (id: "tasks" | "agentBrowser" | "contacts") => void;
}

/** Pure reducer — exported so the behaviour is testable without a DOM. */
export function handleGuidanceCommand(
  payload: ToolEventPayload,
  deps: GuidanceDeps,
): boolean {
  const d = payload?.data;
  if (!d) return false;
  // `start` is the only phase that should act: acting on `end` too would fire
  // every command twice.
  if (d.phase && d.phase !== "start") return false;

  const name = d.name ?? d.toolName;
  const args = d.args ?? d.toolInput ?? {};

  switch (name) {
    case "lx_show_tasklist":
      deps.openFocusPane("tasks");
      return true;

    case "lx_show_agent_browser":
      deps.openFocusPane("agentBrowser");
      return true;

    case "lx_show_agent_channel": {
      const channel = args.channel;
      // An unknown channel is NOT silently coerced to a default: the concierge
      // would then "show" the lawyer the wrong surface while claiming it
      // answered their question. Ignoring is the honest failure.
      if (!isAgentChannel(channel)) return false;
      deps.navigate(`/agent?section=${channel}`);
      return true;
    }

    default:
      return false;
  }
}

export function useGuidanceCommandsFromAgent(): void {
  const navigate = useNavigate();
  const openFocusPane = useUIStore((s) => s.openFocusPane);

  useEventStream(
    "session.tool",
    useCallback(
      (payload: unknown) => {
        handleGuidanceCommand(payload as ToolEventPayload, {
          navigate,
          openFocusPane,
        });
      },
      [navigate, openFocusPane],
    ),
  );
}
